import { BrowseAndBookWalkerProfile, WalkerProfileServiceDetailed } from '../types/api';
import { Snakelize } from '../types/snakelize';

type ResponseProfileFieldTag = Snakelize<{
  description: string;
  isChecked: boolean;
  label: string;
  slug: string;
}>;

type ResponseProfileField = {
  id: string;
  tags: ResponseProfileFieldTag[];
  title: string;
};

type ResponseProfileService = Snakelize<{
  image: string;
  isChecked: boolean;
  label: string;
  slug: string;
}>;

type ResponseProfileServicesDetailed = Snakelize<WalkerProfileServiceDetailed>;

type ResponseProfileTag = {
  image: string;
  label: string;
};

type ResponseProfileBadges = {
  asset: string;
  category: string; // '1'
  count: number;
  description: string;
  id: number;
  label: string;
  weight: number;
};

export type ResponseProfileReview = {
  dogId: number;
  name: string;
  picture: string;
  comment: string;
  rating: number;
  timestamp: string;
  type: string;
};

type ResponseProfileRatesService = {
  icon: string;
  label: 'Walk' | 'Sitting' | 'Boarding' | 'Drop-In';
  value: Lowercase<ResponseProfileRatesService['label']>;
  price: string;
  name?: string;
};

export type ServiceCounts = {
  all: number;
  boarding: number;
  'drop-in': number;
  sitting: number;
  walk: number;
  grooming: number;
  training: number;
  daycare: number;
  vet: number;
  puppy: number;
  overnight: number;
  other: number;
}

export type ResponseWalkerProfile = {
  isPromoted: boolean;
  badges: ResponseProfileBadges[];
  bio: string;
  catchphrase: string;
  fields: ResponseProfileField[];
  isOptIn: boolean;
  link: string;
  name: string;
  picture: string;
  promoCode: string;
  linkLong: string;
  location?: LocationObj;
  rating: number;
  ratingCount: number;
  uuid: string;
  services: ResponseProfileService[];
  servicesDetailed: WalkerProfileServiceDetailed[];
  servicesCount: number;
  serviceCounts?: ServiceCounts;
  tags: ResponseProfileTag[];
  thumbnail: string;
  video: string | null;
  lastServiceCompletedAt: string;
  repeatBookingsCount?: number;
  review: ResponseProfileReview | null;
  rates: {
    description: string;
    services: ResponseProfileRatesService[];
    servicesDetailed: ResponseProfileServicesDetailed[];
  };
  serviceStatus?: {
    description: string;
    icon: string;
    color: string;
  };
}

export type ResponseProfile = Snakelize<ResponseWalkerProfile>;

export type LocationObj = {
  state: string;
  city: string;
  zip: string;
  latitude: number;
  longitude: number;
}

export type GetWalkersByLocationResponse = {
  page: number;
  maxNumberOfPages: number;
  prevPage?: number;
  nextPage?: number;
  profiles: ResponseProfile[];
  service: string;
  state: string;
  zip: string;
};

const responseProfilesFieldsTagsMapper = ({
  slug,
  label,
  description,
  is_checked: isChecked,
}: ResponseProfileFieldTag) => ({
  description,
  isChecked,
  label,
  slug,
});

const responseProfilesFieldsMapper = ({
  id,
  title,
  tags,
}: ResponseProfileField) => ({
  id,
  title,
  tags: tags.map(responseProfilesFieldsTagsMapper),
});

const responseProfilesServicesMapper = ({
  image: iconSrc,
  slug,
  label,
  is_checked: isChecked,
}: ResponseProfileService) => ({
  iconSrc,
  slug,
  label,
  isChecked,
});

const responseProfilesTagsMapper = ({
  image: iconSrc,
  ...rest
}: ResponseProfileTag) => ({
  iconSrc,
  ...rest,
});

export const responseProfilesMapper = ({
  badges,
  bio,
  catchphrase,
  fields,
  link,
  link_long: linkLong,
  name,
  picture,
  rating,
  services,
  thumbnail,
  video,
  location,
  tags,
  uuid,
  is_opt_in: isOptIn,
  promo_code: promoCode,
  rating_count: ratingCount,
  services_count: servicesCount,
  last_service_completed_at: lastServiceCompletedAt,
  review,
  rates,
  service_status,
}: ResponseProfile) => ({
  badges,
  bio,
  catchphrase,
  isOptIn,
  location,
  name,
  picture,
  promoCode,
  linkLong,
  // https://wagsters.atlassian.net/browse/MRKT-1021
  // Context: This was originally a BE change but we opted out to do it on front-end
  // because it affects the android and iOS and had to revert
  rating: Math.ceil(rating * 10) / 10,
  ratingCount,
  servicesCount,
  thumbnail,
  uuid,
  video,
  fields: fields.map(responseProfilesFieldsMapper),
  /**
   * The endpoint returns the link as `https:://wag.me/p/CHRISTOPHER5271`, according
   * to BE, this only happens in slack deploys and this is fixed in production.
   * This is not yet confirmed.
   */
  link: process.env.NODE_ENV === 'development' ? link.replace(':', '') : link,
  services: services.map(responseProfilesServicesMapper),
  tags: tags.map(responseProfilesTagsMapper),
  lastServiceCompletedAt: lastServiceCompletedAt || '',
  review: review || null,
  rates: rates || null,
  serviceStatus: service_status || null,
});

export const getWalkersByLocationResponseTransformer = ({
  profiles,
  state,
  service,
  page: profilesPage,
  nextPage,
  prevPage,
  maxNumberOfPages,
  zip,
}: GetWalkersByLocationResponse) => ({
  state,
  service,
  profilesPage,
  nextPage,
  prevPage,
  maxNumberOfPages,
  zip,
  hasMoreProfiles: !!profiles && !!profiles.length,
  profiles: profiles.map(responseProfilesMapper),
});

export function mapBrowseAndBookWalkerProfileToResponseWalkerProfile(
  profile: BrowseAndBookWalkerProfile
): ResponseWalkerProfile {
  
  return {
    isPromoted: profile.isPromoted ?? false,  // Default to false if missing
    badges: profile?.achievements?.map((badge) => ({
      asset: badge?.asset,
      category: badge?.category,
      count: badge?.count,
      description: '',  // Set a default description if not present
      id: badge?.id,
      label: badge?.label,
      weight: badge?.weight,
    })),
    bio: profile.bio ?? '',  // Default to empty string if missing
    catchphrase: profile.catchphrase ?? '',
    fields: [],
    isOptIn: profile.isPromoted,  // Assuming `isPromoted` implies `isOptIn`, adjust as necessary
    link: profile.link ?? '',
    name: profile.name,
    picture: profile.thumbnail ?? '',
    promoCode: profile.promoCode ?? '',
    linkLong: profile.linkLong ?? '',
    location: profile.location ?? { state: '', city: '', zip: '', latitude: 0, longitude: 0 },
    rating: profile.rating ?? 0,
    ratingCount: profile.ratingCount ?? 0,
    uuid: profile.uuid,
    services: [],
    servicesDetailed: profile.rates.servicesDetailed ?? [],  // Assuming rates has servicesDetailed
    servicesCount: profile.servicesCount ?? 0,
    tags: profile?.ratingBadges?.map((badge) => ({
      image: badge?.imageUrl,
      label: badge?.label,
    })),
    thumbnail: profile.thumbnailUrl ?? '',
    video: profile.videoUrl ?? null,
    lastServiceCompletedAt: profile.lastServiceCompletedAt ?? '',
    review: null,
    rates: {
      description: '',
      services: profile.rates?.services,
      servicesDetailed: []
    },
    serviceStatus: { description: '', icon: '', color: '' },
  };
}
